import type { MessageKey } from '../i18n';

/**
 * Generický command pattern (Epic 13 kap. 5: command vrstva musí existovat
 * PŘED Epic 4, se stejným tvarem, aby se undo/redo zásobník později napojil
 * beze změny existujících command factory funkcí). `canvas/commands.ts`
 * (Epic 6) i `schema/commands.ts` (Epic 4) jsou konkrétní instance tohoto
 * rozhraní nad různým stavem (`CanvasMetadata`, resp. `SchemaModel`).
 *
 * Pravidla, která musí dodržet KAŽDÁ command factory nad tímto rozhraním
 * (viz `canvas/commands.ts` hlavička pro plné odůvodnění):
 * 1. `apply`/`revert` jsou totální a nikdy nevyhazují — validace proběhne
 *    v UI handleru PŘED vytvořením commandu.
 * 2. Nová ID se generují JEDNOU při konstrukci commandu (`randomId()` níže),
 *    nikdy uvnitř `apply()`.
 * 3. `revert()` nese hodnoty odpovídající stavu PŘED mutací (inverzní
 *    hodnota u malých změn, snapshot u rozsáhlých — epic-13 kap. 8).
 * 4. Command sám nikdy nevolá `setState` ani nemutuje svůj vstup — jediné
 *    místo, které smí měnit stav, je `execute()` v příslušném store.
 */
export interface Command<TState, TType extends string = string> {
  readonly type: TType;
  /** Nevyužito dokud nevznikne Epic 13 UI — připraveno pro E13-US3 (popis vratné operace). */
  readonly descriptionKey: MessageKey;
  readonly descriptionParams?: Record<string, string | number>;
  apply(state: TState): TState;
  revert(state: TState): TState;
}

export function randomId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
